/**
 * Token Service
 * 
 * Manages JWT token lifecycle including generation, validation, storage, and refresh.
 * Handles access tokens (15-minute expiry), refresh tokens (30-day expiry), and ID tokens.
 * 
 * Requirements: 1.2, 1.3, 6.1, 6.2, 6.3, 6.4, 6.5
 */

import type {
    AccessTokenPayload,
    IdTokenPayload,
    RefreshTokenPayload,
    TokenSet,
    User,
} from '../types/auth.types';
import { generateRandomString } from '../utils/crypto.utils';
import { getItem, removeItem, setItem } from '../utils/storage.utils';

/**
 * Token validation result
 */
export interface TokenValidation {
  valid: boolean;
  expired: boolean;
  payload?: AccessTokenPayload | RefreshTokenPayload | IdTokenPayload;
  error?: string;
}

/**
 * Decoded token with header and payload
 */
export interface DecodedToken {
  header: {
    alg: string;
    typ: string;
    kid?: string;
  };
  payload: AccessTokenPayload | RefreshTokenPayload | IdTokenPayload;
  signature: string;
}

/**
 * Storage keys for tokens
 */
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'auth_access_token',
  REFRESH_TOKEN: 'auth_refresh_token',
  ID_TOKEN: 'auth_id_token',
} as const;

/**
 * Token expiry times in seconds
 */
const TOKEN_EXPIRY = {
  ACCESS_TOKEN: 15 * 60, // 15 minutes
  REFRESH_TOKEN: 30 * 24 * 60 * 60, // 30 days
  ID_TOKEN: 15 * 60, // 15 minutes
} as const;

/**
 * Issuer for tokens
 */
const TOKEN_ISSUER = 'https://auth.nodegeeks.org';

/**
 * Token Service Class
 * 
 * Provides methods for JWT token management including generation,
 * validation, storage, and refresh operations.
 */
export class TokenService {
  /**
   * Generate an access token
   * 
   * Access tokens are short-lived (15 minutes) and contain user identity
   * and authorization information (roles, permissions).
   * 
   * @param userId - User ID (sub claim)
   * @param email - User email
   * @param name - User display name
   * @param roles - User roles
   * @param permissions - User permissions
   * @param clientId - Client application ID (aud claim)
   * @returns JWT access token string
   */
  generateAccessToken(
    userId: string,
    email: string,
    name: string,
    roles: string[],
    permissions: string[],
    clientId: string
  ): string {
    const now = Math.floor(Date.now() / 1000);
    const exp = now + TOKEN_EXPIRY.ACCESS_TOKEN;
    
    const payload: AccessTokenPayload = {
      sub: userId,
      email,
      name,
      roles,
      permissions,
      iss: TOKEN_ISSUER,
      aud: clientId,
      exp,
      iat: now,
      jti: generateRandomString(32),
    };
    
    return this.encodeToken(payload);
  }
  
  /**
   * Generate a refresh token
   * 
   * Refresh tokens are long-lived (30 days) and used to obtain new
   * access tokens without re-authentication.
   * 
   * @param userId - User ID (sub claim)
   * @param clientId - Client application ID
   * @returns JWT refresh token string
   */
  generateRefreshToken(userId: string, clientId: string): string {
    const now = Math.floor(Date.now() / 1000);
    const exp = now + TOKEN_EXPIRY.REFRESH_TOKEN;
    
    const payload: RefreshTokenPayload = {
      sub: userId,
      clientId,
      iss: TOKEN_ISSUER,
      exp,
      iat: now,
      jti: generateRandomString(32),
    };
    
    return this.encodeToken(payload);
  }
  
  /**
   * Generate an ID token (OIDC compliant)
   * 
   * ID tokens contain user profile information and are used by
   * client applications to identify the authenticated user.
   * 
   * @param user - User object with profile information
   * @param clientId - Client application ID (aud claim)
   * @returns JWT ID token string
   */
  generateIdToken(user: User, clientId: string): string {
    const now = Math.floor(Date.now() / 1000);
    const exp = now + TOKEN_EXPIRY.ID_TOKEN;
    
    const payload: IdTokenPayload = {
      sub: user.id,
      email: user.email,
      email_verified: user.emailVerified,
      name: user.displayName,
      given_name: user.firstName,
      family_name: user.lastName,
      iss: TOKEN_ISSUER,
      aud: clientId,
      exp,
      iat: now,
    };
    
    return this.encodeToken(payload);
  }
  
  /**
   * Validate an access token
   * 
   * Checks token structure, signature (basic check), and expiration.
   * 
   * @param token - JWT access token string
   * @returns Validation result with payload if valid
   */
  validateAccessToken(token: string): TokenValidation {
    try {
      const decoded = this.decodeToken(token);
      const payload = decoded.payload as AccessTokenPayload;
      
      // Check if token is expired
      const now = Math.floor(Date.now() / 1000);
      const expired = payload.exp < now;
      
      // Check issuer
      if (payload.iss !== TOKEN_ISSUER) {
        return {
          valid: false,
          expired: false,
          error: 'Invalid issuer',
        };
      }
      
      // Check required claims
      if (!payload.sub || !payload.email || !payload.aud) {
        return {
          valid: false,
          expired: false,
          error: 'Missing required claims',
        };
      }
      
      return {
        valid: !expired,
        expired,
        payload,
      };
    } catch (error) {
      return {
        valid: false,
        expired: false,
        error: error instanceof Error ? error.message : 'Invalid token',
      };
    }
  }
  
  /**
   * Validate a refresh token
   * 
   * Checks token structure, signature (basic check), and expiration.
   * 
   * @param token - JWT refresh token string
   * @returns Validation result with payload if valid
   */
  validateRefreshToken(token: string): TokenValidation {
    try {
      const decoded = this.decodeToken(token);
      const payload = decoded.payload as RefreshTokenPayload;
      
      // Check if token is expired
      const now = Math.floor(Date.now() / 1000);
      const expired = payload.exp < now;
      
      // Check issuer
      if (payload.iss !== TOKEN_ISSUER) {
        return {
          valid: false,
          expired: false,
          error: 'Invalid issuer',
        };
      }
      
      // Check required claims
      if (!payload.sub || !payload.clientId) {
        return {
          valid: false,
          expired: false,
          error: 'Missing required claims',
        };
      }
      
      return {
        valid: !expired,
        expired,
        payload,
      };
    } catch (error) {
      return {
        valid: false,
        expired: false,
        error: error instanceof Error ? error.message : 'Invalid token',
      };
    }
  }
  
  /**
   * Decode a JWT token
   * 
   * Splits the token into header, payload, and signature components.
   * Does NOT verify the signature - use validate methods for that.
   * 
   * @param token - JWT token string
   * @returns Decoded token with header, payload, and signature
   * @throws Error if token format is invalid
   */
  decodeToken(token: string): DecodedToken {
    const parts = token.split('.');
    
    if (parts.length !== 3) {
      throw new Error('Invalid token format');
    }
    
    const [headerB64, payloadB64, signature] = parts;
    
    // Decode base64url
    const header = JSON.parse(this.base64UrlDecode(headerB64));
    const payload = JSON.parse(this.base64UrlDecode(payloadB64));
    
    return {
      header,
      payload,
      signature: signature,
    };
  }
  
  /**
   * Store tokens in browser storage
   * 
   * Stores access token, refresh token, and ID token in localStorage
   * for persistence across browser sessions.
   * 
   * @param tokens - Token set to store
   */
  storeTokens(tokens: TokenSet): void {
    setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken, 'local');
    setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken, 'local');
    setItem(STORAGE_KEYS.ID_TOKEN, tokens.idToken, 'local');
  }
  
  /**
   * Get access token from storage
   * 
   * @returns Access token or null if not found
   */
  getAccessToken(): string | null {
    return getItem<string>(STORAGE_KEYS.ACCESS_TOKEN, 'local');
  }
  
  /**
   * Get refresh token from storage
   * 
   * @returns Refresh token or null if not found
   */
  getRefreshToken(): string | null {
    return getItem<string>(STORAGE_KEYS.REFRESH_TOKEN, 'local');
  }
  
  /**
   * Get ID token from storage
   * 
   * @returns ID token or null if not found
   */
  getIdToken(): string | null {
    return getItem<string>(STORAGE_KEYS.ID_TOKEN, 'local');
  }
  
  /**
   * Clear all tokens from storage
   * 
   * Removes access token, refresh token, and ID token from localStorage.
   */
  clearTokens(): void {
    removeItem(STORAGE_KEYS.ACCESS_TOKEN, 'local');
    removeItem(STORAGE_KEYS.REFRESH_TOKEN, 'local');
    removeItem(STORAGE_KEYS.ID_TOKEN, 'local');
  }
  
  /**
   * Check if a token is expired
   * 
   * @param token - JWT token string
   * @returns True if token is expired
   */
  isTokenExpired(token: string): boolean {
    try {
      const decoded = this.decodeToken(token);
      const now = Math.floor(Date.now() / 1000);
      return decoded.payload.exp < now;
    } catch {
      return true;
    }
  }
  
  /**
   * Get token expiry timestamp
   * 
   * @param token - JWT token string
   * @returns Expiry timestamp (seconds since epoch) or 0 if invalid
   */
  getTokenExpiry(token: string): number {
    try {
      const decoded = this.decodeToken(token);
      return decoded.payload.exp;
    } catch {
      return 0;
    }
  }
  
  /**
   * Check if token should be refreshed
   * 
   * Returns true if token will expire within 5 minutes.
   * This allows proactive token refresh before expiration.
   * 
   * @param token - JWT token string
   * @returns True if token should be refreshed
   */
  shouldRefreshToken(token: string): boolean {
    try {
      const decoded = this.decodeToken(token);
      const now = Math.floor(Date.now() / 1000);
      const fiveMinutes = 5 * 60;
      return decoded.payload.exp - now < fiveMinutes;
    } catch {
      return true;
    }
  }
  
  /**
   * Encode a token payload to JWT format
   * 
   * Creates a JWT token with header, payload, and signature.
   * Note: This is a simplified implementation for development.
   * In production, use a proper JWT library with RSA/ECDSA signing.
   * 
   * @param payload - Token payload
   * @returns JWT token string
   */
  private encodeToken(payload: AccessTokenPayload | RefreshTokenPayload | IdTokenPayload): string {
    const header = {
      alg: 'HS256',
      typ: 'JWT',
    };
    
    const headerB64 = this.base64UrlEncode(JSON.stringify(header));
    const payloadB64 = this.base64UrlEncode(JSON.stringify(payload));
    
    // In production, this should use proper cryptographic signing
    // For now, we'll use a simple signature for development
    const signature = this.simpleSign(`${headerB64}.${payloadB64}`);
    
    return `${headerB64}.${payloadB64}.${signature}`;
  }
  
  /**
   * Base64 URL encode a string
   * 
   * @param str - String to encode
   * @returns Base64 URL encoded string
   */
  private base64UrlEncode(str: string): string {
    const base64 = btoa(str);
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }
  
  /**
   * Base64 URL decode a string
   * 
   * @param str - Base64 URL encoded string
   * @returns Decoded string
   */
  private base64UrlDecode(str: string): string {
    // Add padding if needed
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    const padding = base64.length % 4;
    if (padding) {
      base64 += '='.repeat(4 - padding);
    }
    return atob(base64);
  }
  
  /**
   * Simple signature for development
   * 
   * In production, this should use HMAC-SHA256 or RSA signing.
   * 
   * @param data - Data to sign
   * @returns Signature string
   */
  private simpleSign(data: string): string {
    // This is a placeholder for development
    // In production, use proper cryptographic signing
    return this.base64UrlEncode(data.split('').reverse().join(''));
  }
}

// Export singleton instance
export const tokenService = new TokenService();
